import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../src/lib/LanguageContext';
import { SupportedLanguage } from '../src/lib/translations';

export const LanguageSelector: React.FC<{ variant?: 'header' | 'sidebar' }> = ({ variant = 'header' }) => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: { code: SupportedLanguage; label: string; flag: string; nativeName: string }[] = [
    { code: 'pt', label: 'Português', flag: '🇧🇷', nativeName: 'BR' },
    { code: 'en', label: 'English', flag: '🇺🇸', nativeName: 'US' },
    { code: 'es', label: 'Español', flag: '🇪🇸', nativeName: 'ES' },
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (variant === 'sidebar') {
    return (
      <div className="relative w-full" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-2.5 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl text-slate-400 hover:text-white transition-all text-xs font-bold uppercase tracking-wider"
        >
          <div className="flex items-center gap-2">
            <span className="text-base leading-none">{currentLang.flag}</span>
            <span>{currentLang.label}</span>
          </div>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-350 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute bottom-full left-0 w-full mb-1.5 bg-[#0b0c10] border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider transition-colors ${
                  language === lang.code 
                    ? 'bg-indigo-500/10 text-white' 
                    : 'text-slate-400 hover:bg-white/[0.03] hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-sm leading-none">{lang.flag}</span>
                  <span>{lang.label}</span>
                </div>
                {language === lang.code && <Check className="w-3.5 h-3.5 text-indigo-400" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-3 py-1.5 bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-full text-slate-300 hover:text-white transition-all text-[11px] font-black uppercase tracking-widest cursor-pointer group"
      >
        <Globe className="w-3.5 h-3.5 text-indigo-400 group-hover:rotate-12 transition-transform duration-500" />
        <span className="flex items-center gap-1">
          {currentLang.flag} {currentLang.nativeName}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-350 opacity-60 group-hover:opacity-100 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-44 bg-[#0d0e12] border border-white/10 rounded-2xl overflow-hidden shadow-2xl z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2 border-b border-white/5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Select Language | Idioma
          </div>
          <div className="p-1.5 space-y-0.5">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left text-[11px] font-black uppercase tracking-wider transition-all duration-150 ${
                  language === lang.code 
                    ? 'bg-gradient-to-r from-indigo-500/20 to-violet-500/10 border border-indigo-500/20 text-white shadow-sm' 
                    : 'text-slate-400 hover:bg-white/[0.03] hover:text-white border border-transparent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-base leading-none">{lang.flag}</span>
                  <span>{lang.label}</span>
                </div>
                {language === lang.code && <Check className="w-3.5 h-3.5 text-indigo-400" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
